const CHECK_TIMEOUT_MS = 5_000;
const UNREGISTER_TIMEOUT_MS = 3_000;

/** The hashed entry script identifies the release whose JavaScript is actually running. */
export function appEntryFromHtml(html: string, origin: string): string | null {
  const document = new DOMParser().parseFromString(html, 'text/html');
  return appEntryFromDocument(document, origin);
}

export function appEntryFromDocument(document: Document, origin: string): string | null {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const url = new URL(script.getAttribute('src') ?? '', origin);
    if (url.origin === origin && /\/assets\/index-[^/]+\.js$/.test(url.pathname)) {
      return url.pathname;
    }
  }
  return null;
}

interface AppVersionMonitorOptions {
  entry: string | null;
  origin: string;
  onUpdateRequired: () => void;
  fetchHtml?: typeof fetch;
}

export function createAppVersionMonitor({
  entry,
  origin,
  onUpdateRequired,
  fetchHtml = (...args) => fetch(...args),
}: AppVersionMonitorOptions) {
  let stale = false;
  let pending: Promise<boolean | null> | undefined;

  async function checkRelease(): Promise<boolean | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
    try {
      // The unique query bypasses old Workbox precache entries, not just the HTTP cache.
      const url = new URL('/index.html', origin);
      url.searchParams.set(
        'alamo-version-check',
        `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      );
      const response = await fetchHtml(url.href, {
        cache: 'no-store',
        credentials: 'omit',
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const published = appEntryFromHtml(await response.text(), origin);
      if (!published) return null;
      if (published !== entry) {
        stale = true;
        onUpdateRequired();
        return false;
      }
      return true;
    } catch {
      // An unavailable release check is a network condition, not evidence of a new release.
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  return {
    check(): Promise<boolean | null> {
      if (stale) return Promise.resolve(false);
      if (!entry) return Promise.resolve(null);
      pending ??= checkRelease().finally(() => {
        pending = undefined;
      });
      return pending;
    },
  };
}

/** Browser registration jobs must settle before navigation can safely bypass the old worker. */
function detachWorker(
  registration: ServiceWorkerRegistration,
  signal?: AbortSignal,
): Promise<void> {
  signal?.throwIfAborted();
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const onAbort = () => fail(signal?.reason);
    const timeout = setTimeout(
      () => fail(new Error('SERVICE_WORKER_UNREGISTER_TIMEOUT')),
      UNREGISTER_TIMEOUT_MS,
    );
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      void registration.unregister().then(finish, fail);
    } catch (error) {
      fail(error);
    }
  });
}

/** Activating a worker is not enough: the page must reload to execute its new JavaScript. */
export async function reloadWithCurrentWorker(
  registration: ServiceWorkerRegistration | null,
  container: ServiceWorkerContainer | undefined,
  reload: () => void,
  signal?: AbortSignal,
): Promise<void> {
  signal?.throwIfAborted();
  const waiting = registration?.waiting;
  if (waiting && container) {
    try {
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          clearTimeout(timeout);
          container.removeEventListener('controllerchange', finish);
          waiting.removeEventListener('statechange', onStateChange);
          signal?.removeEventListener('abort', onAbort);
        };
        const finish = () => {
          cleanup();
          resolve();
        };
        const onAbort = () => {
          cleanup();
          reject(signal?.reason);
        };
        const onStateChange = () => {
          // Activation can finish without controllerchange reaching this document.
          if (waiting.state === 'activated') finish();
        };
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error('SERVICE_WORKER_ACTIVATION_TIMEOUT'));
        }, 10_000);
        container.addEventListener('controllerchange', finish);
        waiting.addEventListener('statechange', onStateChange);
        signal?.addEventListener('abort', onAbort, { once: true });
        if (waiting.state === 'activated') finish();
        else {
          try {
            waiting.postMessage({ type: 'SKIP_WAITING' });
          } catch (error) {
            cleanup();
            reject(error);
          }
        }
      });
    } catch {
      signal?.throwIfAborted();
      // A worker that ignores SKIP_WAITING must not trap the user in a retry loop.
      // The user already chose to reload; detach this registration for that navigation.
      await detachWorker(registration, signal);
    }
  } else if (registration) {
    // A bridge worker may already have claimed the page while its old entry remains in memory.
    // Removing the registration lets the next navigation obtain the current HTML from the network.
    await detachWorker(registration, signal);
  }
  signal?.throwIfAborted();
  reload();
}
